import { Pixel } from "../canvas/types";

export enum ColourConversionAlgorithm {
    EUCLIDEAN = "euclidean", // Simple RGB distance
    LAB = "lab", // CIE LAB color space
    PERCEPTUAL = "perceptual", // Weighted RGB for human perception
    DITHER = "dither", // Floyd-Steinberg dithering
}

export interface ColourConversionResult {
    pixels: Array<Pixel>;
    width: number;
    height: number;
}

export interface RGB {
    r: number;
    g: number;
    b: number;
}

export interface LAB {
    l: number;
    a: number;
    b: number;
}

export class ColourConverter {
    private readonly palette: string[];

    constructor(palette: string[]) {
        this.palette = palette;
    }

    public convertImage(
        imageData: ImageData,
        algorithm: ColourConversionAlgorithm
    ): ColourConversionResult {
        switch (algorithm) {
            case ColourConversionAlgorithm.LAB:
                return this.convertWithLAB(imageData);
            case ColourConversionAlgorithm.PERCEPTUAL:
                return this.convertWithPerceptual(imageData);
            case ColourConversionAlgorithm.DITHER:
                return this.convertWithDithering(imageData);
            case ColourConversionAlgorithm.EUCLIDEAN:
            default:
                return this.convertWithEuclidean(imageData);
        }
    }

    private convertWithEuclidean(imageData: ImageData): ColourConversionResult {
        const pixels: Array<Pixel> = [];
        const width = imageData.width;
        const height = imageData.height;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;
                const r = imageData.data[i];
                const g = imageData.data[i + 1];
                const b = imageData.data[i + 2];
                const colour = this.findClosestColourEuclidean(r, g, b);
                pixels.push({x, y, colour});
            }
        }

        return {pixels, width, height};
    }

    private convertWithLAB(imageData: ImageData): ColourConversionResult {
        const pixels: Array<Pixel> = [];
        const width = imageData.width;
        const height = imageData.height;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;
                const r = imageData.data[i];
                const g = imageData.data[i + 1];
                const b = imageData.data[i + 2];
                const colour = this.findClosestColourLAB(r, g, b);
                pixels.push({x, y, colour});
            }
        }

        return {pixels, width, height};
    }

    private convertWithPerceptual(imageData: ImageData): ColourConversionResult {
        const pixels: Array<Pixel> = [];
        const width = imageData.width;
        const height = imageData.height;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;
                const r = imageData.data[i];
                const g = imageData.data[i + 1];
                const b = imageData.data[i + 2];
                const colour = this.findClosestColourPerceptual(r, g, b);
                pixels.push({x, y, colour});
            }
        }

        return {pixels, width, height};
    }

    private convertWithDithering(imageData: ImageData): ColourConversionResult {
        const pixels: Array<Pixel> = [];
        const width = imageData.width;
        const height = imageData.height;

        // Create a copy of the image data to apply error diffusion
        const data = new Uint8ClampedArray(imageData.data);

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];

                // Find closest colour
                const colour = this.findClosestColourEuclidean(r, g, b);
                pixels.push({x, y, colour});

                // Calculate error
                const closest = this.hexToRgb(colour);
                if (!closest) continue;

                const errorR = r - closest.r;
                const errorG = g - closest.g;
                const errorB = b - closest.b;

                // Distribute error (Floyd-Steinberg dithering)
                if (x + 1 < width) {
                    data[i + 4] += (errorR * 7) / 16;
                    data[i + 5] += (errorG * 7) / 16;
                    data[i + 6] += (errorB * 7) / 16;
                }

                if (y + 1 < height) {
                    if (x > 0) {
                        data[i + width * 4 - 4] += (errorR * 3) / 16;
                        data[i + width * 4 - 3] += (errorG * 3) / 16;
                        data[i + width * 4 - 2] += (errorB * 3) / 16;
                    }

                    data[i + width * 4] += (errorR * 5) / 16;
                    data[i + width * 4 + 1] += (errorG * 5) / 16;
                    data[i + width * 4 + 2] += (errorB * 5) / 16;

                    if (x + 1 < width) {
                        data[i + width * 4 + 4] += errorR /* x1 */ / 16;
                        data[i + width * 4 + 5] += errorG /* x1 */ / 16;
                        data[i + width * 4 + 6] += errorB /* x1 */ / 16;
                    }
                }
            }
        }

        return {pixels, width, height};
    }

    private findClosestColourEuclidean(r: number, g: number, b: number): string {
        let minDistance = Infinity;
        let closestColour = this.palette[0];

        for (const colour of this.palette) {
            const rgb = this.hexToRgb(colour);
            if (!rgb) continue;

            const distance = Math.sqrt(
                Math.pow(r - rgb.r, 2) + Math.pow(g - rgb.g, 2) + Math.pow(b - rgb.b, 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                closestColour = colour;
            }
        }

        return closestColour;
    }

    private findClosestColourLAB(r: number, g: number, b: number): string {
        const lab1 = this.rgbToLab({r, g, b});
        let minDistance = Infinity;
        let closestColour = this.palette[0];

        for (const colour of this.palette) {
            const rgb = this.hexToRgb(colour);
            if (!rgb) continue;

            const lab2 = this.rgbToLab(rgb);
            const distance = Math.sqrt(
                Math.pow(lab1.l - lab2.l, 2) +
                Math.pow(lab1.a - lab2.a, 2) +
                Math.pow(lab1.b - lab2.b, 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                closestColour = colour;
            }
        }

        return closestColour;
    }

    private findClosestColourPerceptual(r: number, g: number, b: number): string {
        let minDistance = Infinity;
        let closestColour = this.palette[0];

        // Weights based on human perception
        const rw = 0.299;
        const gw = 0.587;
        const bw = 0.114;

        for (const colour of this.palette) {
            const rgb = this.hexToRgb(colour);
            if (!rgb) continue;

            const distance = Math.sqrt(
                rw * Math.pow(r - rgb.r, 2) +
                gw * Math.pow(g - rgb.g, 2) +
                bw * Math.pow(b - rgb.b, 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                closestColour = colour;
            }
        }

        return closestColour;
    }

    public hexToRgb(hex: string): RGB|null {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
            }
            : null;
    }

    private rgbToLab(rgb: RGB): LAB {
        // Convert RGB to XYZ
        let r = rgb.r / 255;
        let g = rgb.g / 255;
        let b = rgb.b / 255;

        r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
        g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
        b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

        const x = (r * 0.4124 + g * 0.3576 + b * 0.1805) * 100;
        const y = (r * 0.2126 + g * 0.7152 + b * 0.0722) * 100;
        const z = (r * 0.0193 + g * 0.1192 + b * 0.9505) * 100;

        // Convert XYZ to Lab
        const xn = 95.047;
        const yn = 100.0;
        const zn = 108.883;

        const xyz = [x / xn, y / yn, z / zn].map((v) =>
            v > 0.008856 ? Math.pow(v, 1 / 3) : 7.787 * v + 16 / 116
        );

        return {
            l: 116 * xyz[1] - 16,
            a: 500 * (xyz[0] - xyz[1]),
            b: 200 * (xyz[1] - xyz[2]),
        };
    }

    public convertToRgbaArray(
        pixels: Array<Pixel>,
        width: number,
        height: number
    ): Uint8ClampedArray {
        const data = new Uint8ClampedArray(width * height * 4);
        pixels.forEach((pixel) => {
            const rgb = this.hexToRgb(pixel.colour);
            if (!rgb) return;

            const i = (pixel.y * width + pixel.x) * 4;
            data[i] = rgb.r;
            data[i + 1] = rgb.g;
            data[i + 2] = rgb.b;
            data[i + 3] = 255;
        });
        return data;
    }
}
